import { Component } from '@theme/component';
import { ThemeEvents, MediaStartedPlayingEvent } from '@theme/events';
import { DialogCloseEvent } from '@theme/dialog';

/**
 * A deferred media element
 * @typedef {Object} Refs
 * @property {HTMLElement} deferredMediaPlayButton - The button to show the deferred media content
 * @property {HTMLElement} toggleMediaButton - The button to toggle the media
 * @property {HTMLElement} toggleVolumeButton - The button to toggle the volume
 *
 * @extends {Component<Refs>}
 */
class DeferredMedia extends Component {
  /** @type {boolean} */
  isPlaying = false;

  /** @type {boolean} */
  isMuted = false;

  #abortController = new AbortController();
  #intersectionObserver = null;

  connectedCallback() {
    super.connectedCallback();
    const signal = this.#abortController.signal;

    if (this.hasAttribute('autoplay')) {
      this.isPlaying = true;
    }

    const toggleVolumeButton = this.refs.toggleVolumeButton;
    if (toggleVolumeButton) {
      this.isMuted = toggleVolumeButton.dataset.muted === 'true';
      this.updateVolumeHint(this.isMuted);
    }

    // If we're to use deferred media for images, we will need to run this only when it's not an image type media
    document.addEventListener(ThemeEvents.mediaStartedPlaying, this.pauseMedia.bind(this), {
      signal,
    });
    window.addEventListener(DialogCloseEvent.eventName, this.pauseMedia.bind(this), { signal });

    // Handle slideshow selection to trigger media loading
    document.addEventListener(
      'slideshow:select',
      (event) => {
        // @ts-ignore
        const { slide } = event.detail;
        if (slide?.contains(this)) {
          this.loadContent(false);
        }
      },
      { signal }
    );

    // Handle zoom dialog selection to trigger media loading
    document.addEventListener(
      ThemeEvents.zoomMediaSelected,
      (event) => {
        // @ts-ignore
        const { index } = event.detail;
        const zoomDialog = event.target;
        // @ts-ignore
        if (zoomDialog?.refs?.media?.[index]?.contains(this)) {
          this.loadContent(false);
        }
      },
      { signal }
    );

    this.initLoadingStrategy();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
    if (this.#intersectionObserver) {
      this.#intersectionObserver.disconnect();
    }
  }

  /**
   * Initialize the loading strategy based on attributes
   */
  initLoadingStrategy() {
    this.loadOnInteraction = this.getAttribute('data-load-on-interaction') === 'true';
    this.loadOnClick = this.getAttribute('data-load-on-click') === 'true';
    this.userInteracted = false;
    this.isIntersecting = false;

    // Usercentrics DPS scanner: bypass interaction/intersection gating so
    // embedded media (YouTube/Vimeo) fires and can be catalogued.
    if (window.__ucScanner) {
      this.loadOnInteraction = false;
      this.loadOnClick = false;
      this.userInteracted = true;
      this.isIntersecting = true;
      this.tryLoad();
      return;
    }

    this.initIntersectionObserver();

    if (this.loadOnInteraction) {
      this.addInteractionListeners();
    }
  }

  /**
   * Set up intersection observer
   */
  initIntersectionObserver() {
    const options = {
      root: null,
      rootMargin: '300px',
      threshold: 0,
    };

    this.#intersectionObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        this.isIntersecting = entry.isIntersecting;
        if (this.isIntersecting) {
          this.tryLoad();
        }
      });
    }, options);

    this.#intersectionObserver.observe(this);
  }

  /**
   * Add listeners for user interaction
   */
  addInteractionListeners() {
    const interactionEvents = ['pointerdown', 'keydown', 'wheel', 'touchstart'];
    const options = { passive: true, once: true };

    const interactionHandler = () => {
      this.userInteracted = true;
      this.tryLoad();

      // Remove all listeners once one fires
      interactionEvents.forEach((event) => {
        window.removeEventListener(event, interactionHandler);
      });
    };

    interactionEvents.forEach((event) => {
      window.addEventListener(event, interactionHandler, options);
    });
  }

  /**
   * Check conditions and load if met
   */
  tryLoad() {
    if (this.getAttribute('data-media-loaded')) return;

    if (this.loadOnInteraction && !this.userInteracted) return;

    if (this.loadOnClick) return;

    if (!this.isIntersecting) return;

    this.loadContentFromStrategy();

    // Disconnect observer once loaded
    if (this.#intersectionObserver) {
      this.#intersectionObserver.disconnect();
      this.#intersectionObserver = null;
    }
  }

  /**
   * Internal method to trigger loading based on strategy
   */
  loadContentFromStrategy() {
    const shouldAutoplay = this.hasAttribute('data-autoplay') || this.hasAttribute('autoplay');
    this.loadContentFromIntersection(shouldAutoplay);
  }

  /**
   * Updates the visual hint for play/pause state
   * @param {boolean} isPlaying - Whether the video is currently playing
   */
  updatePlayPauseHint(isPlaying) {
    const toggleMediaButton = this.refs.toggleMediaButton;
    if (toggleMediaButton instanceof HTMLElement) {
      toggleMediaButton.classList.remove('hidden');
      const playIcon = toggleMediaButton.querySelector('.icon-play');
      if (playIcon) playIcon.classList.toggle('hidden', isPlaying);
      const pauseIcon = toggleMediaButton.querySelector('.icon-pause');
      if (pauseIcon) pauseIcon.classList.toggle('hidden', !isPlaying);
    }
  }

  updateVolumeHint(isMuted) {
    const toggleVolumeButton = this.refs.toggleVolumeButton;
    if (toggleVolumeButton instanceof HTMLElement) {
      const volumeIcon = toggleVolumeButton.querySelector('.icon-volume');
      if (volumeIcon) volumeIcon.classList.toggle('hidden', isMuted);
      const muteIcon = toggleVolumeButton.querySelector('.icon-mute');
      if (muteIcon) muteIcon.classList.toggle('hidden', !isMuted);
    }
  }

  /**
   * Shows the deferred media content
   */
  showDeferredMedia = () => {
    this.refs.deferredMediaPlayButton?.classList.add('deferred-media__playing');

    if (this.getAttribute('data-media-loaded')) {
      this.playMedia();
      return;
    }

    this.loadContent(true);
    this.isPlaying = true;
    this.updatePlayPauseHint(this.isPlaying);
  };

  /**
   * Loads the content
   * @param {boolean} [focus] - Whether to focus the content
   */
  loadContent(focus = true) {
    if (this.getAttribute('data-media-loaded')) {
      return;
    }

    this.dispatchEvent(new MediaStartedPlayingEvent(this));

    const template = this.querySelector('template');
    const content = template?.content.firstElementChild?.cloneNode(true);

    if (!content) {
      return;
    }

    this.setAttribute('data-media-loaded', 'true');
    this.appendChild(content);

    if (focus && content instanceof HTMLElement) {
      content.focus();
    }

    if (content instanceof HTMLVideoElement) {
      // force autoplay for safari
      content.muted = this.isMuted;
      content.play();
    }
  }

  /**
   * Public method to load content from external calls (e.g., Alpine.js)
   * @param {boolean} shouldAutoplay - Whether to autoplay after loading
   */
  loadContentFromIntersection(shouldAutoplay = false) {
    this.loadContent(false);
    if (shouldAutoplay) {
      // Initialize muted state for autoplay videos
      const toggleVolumeButton = this.refs.toggleVolumeButton;
      if (toggleVolumeButton && toggleVolumeButton.dataset.muted === 'true') {
        this.isMuted = true;
      } else if (!toggleVolumeButton) {
        // Default to muted for autoplay videos without volume control
        this.isMuted = true;
      }

      // Wait for content to be added to the DOM and iframe to be ready
      requestAnimationFrame(() => {
        setTimeout(() => {
          this.playMedia();
        }, 300);
      });
    }
  }

  /**
   * Toggle play/pause state of the media
   */
  toggleMedia() {
    if (this.isPlaying) {
      this.pauseMedia();
    } else {
      this.playMedia();
    }
  }

  toggleVolume() {
    this.isMuted = !this.isMuted;
    this.updateVolumeHint(this.isMuted);

    const iframe = this.querySelector('iframe[data-video-type]');
    if (iframe) {
      if (iframe.dataset.videoType === 'youtube') {
        iframe.contentWindow?.postMessage(
          `{"event":"command","func":"${this.isMuted ? 'mute' : 'unMute'}","args":""}`,
          '*'
        );
      } else if (iframe.dataset.videoType === 'vimeo') {
        iframe.contentWindow?.postMessage(
          `{"method":"${this.isMuted ? 'setVolume' : 'setVolume'}","value":${this.isMuted ? '0' : '1'}}`,
          '*'
        );
      }
    } else {
      const video = this.querySelector('video');
      if (video) {
        video.muted = this.isMuted;
      }
    }
  }

  playMedia() {
    this.refs.deferredMediaPlayButton?.classList.add('deferred-media__playing');

    /** @type {HTMLIFrameElement | null} */
    const iframe = this.querySelector('iframe[data-video-type]');
    if (iframe) {
      iframe.contentWindow?.postMessage(
        iframe.dataset.videoType === 'youtube'
          ? '{"event":"command","func":"playVideo","args":""}'
          : '{"method":"play"}',
        '*'
      );

      // Apply mute state to iframe videos
      if (iframe.dataset.videoType === 'youtube') {
        iframe.contentWindow?.postMessage(
          `{"event":"command","func":"${this.isMuted ? 'mute' : 'unMute'}","args":""}`,
          '*'
        );
      } else if (iframe.dataset.videoType === 'vimeo') {
        iframe.contentWindow?.postMessage(
          `{"method":"setVolume","value":${this.isMuted ? '0' : '1'}}`,
          '*'
        );
      }
    } else {
      const video = this.querySelector('video');
      if (video) {
        video.muted = this.isMuted;
        video.play();
      }
    }
    this.isPlaying = true;
    this.updatePlayPauseHint(this.isPlaying);
  }

  /**
   * Pauses the media
   */
  pauseMedia() {
    /** @type {HTMLIFrameElement | null} */
    const iframe = this.querySelector('iframe[data-video-type]');

    if (iframe) {
      iframe.contentWindow?.postMessage(
        iframe.dataset.videoType === 'youtube'
          ? '{"event":"command","func":"' + 'pauseVideo' + '","args":""}'
          : '{"method":"pause"}',
        '*'
      );
    } else {
      this.querySelector('video')?.pause();
    }
    this.isPlaying = false;

    // If we've already revealed the deferred media, we should toggle the play/pause hint
    if (this.getAttribute('data-media-loaded') || this.hasAttribute('autoplay')) {
      this.updatePlayPauseHint(this.isPlaying);
    }
  }
}

if (!customElements.get('deferred-media')) {
  customElements.define('deferred-media', DeferredMedia);
}

/**
 * A product model
 */
class ProductModel extends DeferredMedia {
  #abortController = new AbortController();

  loadContent() {
    super.loadContent();

    Shopify.loadFeatures([
      {
        name: 'model-viewer-ui',
        version: '1.0',
        onLoad: this.setupModelViewerUI.bind(this),
      },
    ]);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
  }

  pauseMedia() {
    super.pauseMedia();
    this.modelViewerUI?.pause();
  }

  playMedia() {
    super.playMedia();
    this.modelViewerUI?.play();
  }

  /**
   * @param {Error[]} errors
   */
  async setupModelViewerUI(errors) {
    if (errors) return;

    if (!Shopify.ModelViewerUI) {
      await this.#waitForModelViewerUI();
    }

    if (!Shopify.ModelViewerUI) return;

    const element = this.querySelector('model-viewer');
    if (!element) return;

    const signal = this.#abortController.signal;

    this.modelViewerUI = new Shopify.ModelViewerUI(element);
    if (!this.modelViewerUI) return;

    this.playMedia();

    // Track pointer events to detect taps
    let pointerStartX = 0;
    let pointerStartY = 0;

    element.addEventListener(
      'pointerdown',
      (/** @type {PointerEvent} */ event) => {
        pointerStartX = event.clientX;
        pointerStartY = event.clientY;
      },
      { signal }
    );

    element.addEventListener(
      'click',
      (/** @type {PointerEvent} */ event) => {
        const distanceX = Math.abs(event.clientX - pointerStartX);
        const distanceY = Math.abs(event.clientY - pointerStartY);
        const totalDistance = Math.sqrt(distanceX * distanceX + distanceY * distanceY);

        // Try to ensure that this is a tap, not a drag.
        if (totalDistance < 10) {
          // When the model is paused, it has its own button overlay for playing the model again.
          // If we're receiving a click event, it means the model is playing, all we can do is pause it.
          this.pauseMedia();
        }
      },
      { signal }
    );
  }

  /**
   * Waits for Shopify.ModelViewerUI to be defined.
   * This seems to be necessary for Safari since Shopify.ModelViewerUI is always undefined on the first try.
   * @returns {Promise<void>}
   */
  async #waitForModelViewerUI() {
    const maxAttempts = 10;
    const interval = 50;

    for (let i = 0; i < maxAttempts; i++) {
      if (Shopify.ModelViewerUI) {
        return;
      }
      await new Promise((resolve) => setTimeout(resolve, interval));
    }
  }
}

if (!customElements.get('product-model')) {
  customElements.define('product-model', ProductModel);
}
